import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getClientUserEmployeeInfo, getClientUserEmployeeLeaves } from "api/apiHelper";
import type { EmployeeLeavesDetailsType } from "types/leaves-types";
import { LeaveDetailsType } from "pages/leaves/Leaves";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import LeavesStatusTable from "features/leaves/LeavesStatusTable";
import Card from "components/Card";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const ClientEmployeeDetail = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const [employee, setEmployee] = useState<any>(null);
  const [leaves, setLeaves] = useState<EmployeeLeavesDetailsType[]>([]);
  const [loading, setLoading] = useState(false);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationData, setPaginationData] = useState<PaginatedResponse<EmployeeLeavesDetailsType> | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const fetchEmployeeDetails = async () => {
    if (!employeeId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getClientUserEmployeeInfo(employeeId);
      setEmployee(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load employee information.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeLeaves = async (size: number = pageSize, pageUrl?: string) => {
    if (!employeeId) return;

    try {
      setLeavesLoading(true);
      setError(null);
      let response;
      if (pageUrl) {
        response = await getClientUserEmployeeLeaves(employeeId, undefined, pageUrl);
      } else {
        const params: any = {
          page_size: size,
        };
        response = await getClientUserEmployeeLeaves(employeeId, params);
      }

      if (response.data?.results) {
        setPaginationData(response.data);
        setLeaves(response.data.results);
      } else if (Array.isArray(response.data)) {
        setLeaves(response.data);
        setPaginationData(null);
      } else {
        setLeaves([]);
        setPaginationData(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load employee leaves.");
      setLeaves([]);
      setPaginationData(null);
    } finally {
      setLeavesLoading(false);
    }
  };

  const handlePageSizeChange = async (newSize: number) => {
    setPageSize(newSize);
    await fetchEmployeeLeaves(newSize);
  };

  const handleNextPage = async () => {
    if (paginationData?.next) {
      await fetchEmployeeLeaves(pageSize, paginationData.next);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = async () => {
    if (paginationData?.previous) {
      await fetchEmployeeLeaves(pageSize, paginationData.previous);
      setCurrentPage((prev) => prev - 1);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchEmployeeDetails();
      fetchEmployeeLeaves();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  if (loading && !employee) return <Backdrop show={true} />;

  return (
    <div className="mt-5">
      {error && <ErrorBox errorMessage={error} />}

      {/* Employee Information Card */}
      {employee && (
        <Card extra="p-6 mb-6">
          <h2 className="text-xl font-semibold text-navy-700 dark:text-white mb-4">
            Employee Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              {employee.photo ? (
                <img
                  src={employee.photo}
                  alt={employee.full_name || "Employee"}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-navy-700 flex items-center justify-center">
                  <i className="fas fa-user text-3xl text-gray-400"></i>
                </div>
              )}
              <div>
                <p className="font-semibold text-lg text-navy-700 dark:text-white">
                  {employee.full_name || "N/A"}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {employee.email || "N/A"}
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Employee ID</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">
                #{employee.id}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Phone</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">
                {employee.phone || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Designation</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">
                {employee.designation || "N/A"}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Employee Leaves Table */}
      <LeavesStatusTable
        tableData={leaves as LeaveDetailsType[]}
        tableHeading={`Leave Requests - ${employee ? employee.full_name : 'Employee'}`}
        totalCount={paginationData?.count}
        currentPage={currentPage}
        pageSize={pageSize}
        hasNextPage={!!paginationData?.next}
        hasPreviousPage={!!paginationData?.previous}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

export default ClientEmployeeDetail;
